import { lazy, type ComponentType } from 'react';
import { reloadOnceForStaleChunk } from './chunkReload';

const sleep = (ms: number) => new Promise<void>(r => setTimeout(r, ms));

/**
 * lazy() that survives a slow network and a deploy without flashing the error page.
 *
 * Two failure modes when a tab view lazy-loads:
 *   1. Transient — the chunk just didn't arrive in time (flaky/slow network). A quick
 *      retry usually clears it with no reload and nothing shown to the user.
 *   2. Stale — a new deploy replaced the old hashed chunk, so it 404s. Here we reload
 *      once to pull the fresh bundle. Crucially we then HOLD in a pending state (the
 *      Suspense fallback keeps showing) instead of throwing, so the branded error
 *      page never flashes while the reload is in flight — this is the race that made
 *      the "hit a snag" card blink before the page auto-recovered. The reload is
 *      coordinated through a single shared guard (reloadOnceForStaleChunk), so it
 *      doesn't matter whether we or main.tsx's `vite:preloadError` handler wins.
 *
 * If a reload is triggered but never actually takes over within a few seconds (e.g. a
 * genuinely-broken chunk that survives the reload, so the guard suppresses a second
 * one), we finally reject so the ErrorBoundary can show its recovery card — the user
 * is never stuck on an infinite spinner.
 */
export function lazyWithReload<T extends ComponentType<any>>(
  factory: () => Promise<{ default: T }>,
) {
  return lazy(async () => {
    try {
      return await factory();
    } catch {
      // 1) One quick retry for a transient miss.
      try {
        await sleep(500);
        return await factory();
      } catch (err) {
        // 2) Still failing → treat as a stale chunk. Reload once (coordinated) and
        //    hold so no error card flashes. Fall through to reject only if the
        //    reload doesn't take over in time.
        reloadOnceForStaleChunk();
        return new Promise<{ default: T }>((_, reject) => {
          setTimeout(() => reject(err), 6000);
        });
      }
    }
  });
}
